import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';

import * as graphql from '@treams/treams-network';
import {
	Button,
	Callout,
	GraphQLErrorMessage,
	Heading3,
	Input,
	Paragraph,
	Spinner,
} from '@treams/treams-ui';

export default function Azure() {
	const [step, setStep] = useState(0);
	const [url, setURL] = useState('');
	const [createdProviderId, setCreatedProviderId] = useState<string | null>(null);
	const history = useHistory();

	const [create, { loading, error }] = graphql.useCreateIdentityProviderMutation();

	const query = graphql.useServiceProvidersQuery();

	async function onMetadataURL() {
		try {
			const createResult = await create({ variables: { input: { metadataURL: url } } });
			const newProviderId = createResult.data?.createIdentityProvider?.provider?.id;
			if (newProviderId) {
				setCreatedProviderId(newProviderId);
			}

			await query.refetch();
			setStep(step + 1);
		} catch {
			// create function returns both errors and throws things
		}
	}

	if (!query.data) {
		return <Spinner />;
	}

	return (
		<>
			<Box mb={3}>
				<Heading3>2. Maak een nieuwe SAML-koppeling aan in Azure.</Heading3>
				<Box ml={3}>
					<Paragraph>
						<ol>
							<li>Open het Azure admin panel.</li>
							<li>
								Ga in het menu links naar <strong>enterprise applications</strong>.
							</li>
							<li>
								Klik op <strong>New application</strong>.
							</li>
							<li>
								Selecteer <strong>Non-galery application</strong> en klik op <strong>Add</strong>.
							</li>
							<li>
								Ga vervolgens naar stap 2 <strong>Sign-in</strong>.
							</li>
							<li>
								Kies in dit scherm voor <strong>SAML</strong>.
							</li>
							<li>
								Klik nu op <strong>Upload metadata XML</strong>.
							</li>
						</ol>
					</Paragraph>
				</Box>
				{step === 0 && (
					<Flex>
						<Button title="Volgende stap" onClick={() => setStep((step) => step + 1)} />
					</Flex>
				)}
			</Box>
			{step >= 1 && (
				<Box mb={3}>
					<Heading3>3. Upload onze metadata XML naar Azure</Heading3>
					<Paragraph>
						In Azure wordt er gevraagd om een <strong>metadata bestand</strong> te uploaden. Er zijn
						dan twee mogelijkheden:
						<Box ml={3}>
							<ol>
								<li>
									Vul de volgende URL in het vak van de bestandsnaam in:
									<Flex alignItems="center" mt={2}>
										<Box flexGrow={1} flexShrink={1} mr={2}>
											<Input
												value={query.data.serviceProvider.metadataURL}
												onChange={() => {}}
												disabled
											/>
										</Box>
										<Button
											title="Kopiëren"
											type="secondary"
											onClick={async () => {
												await navigator.clipboard.writeText(
													query.data!.serviceProvider.metadataURL,
												);
											}}
										/>
									</Flex>
								</li>
								<li>
									<strong>of</strong> download het onderstaande bestand en upload dat vervolgens in
									Azure:
									<Flex mt={2} alignItems="center">
										<a
											href={query.data.serviceProvider.metadataURL}
											target="_blank"
											download="metadata.xml"
											rel="noreferrer"
										>
											<Button type="secondary" title="Downloaden" />
										</a>
									</Flex>
								</li>
							</ol>
						</Box>
						Klik hierna op <strong>Save</strong>.
					</Paragraph>

					{step === 1 && (
						<Flex mt={2}>
							<Button title="Volgende stap" onClick={() => setStep((step) => step + 1)} />
						</Flex>
					)}
				</Box>
			)}
			{step >= 2 && (
				<Box mb={3}>
					<Heading3>4. Geef jullie metadata XML terug aan ons</Heading3>
					<Paragraph>
						Kopieer de URL uit het grijze <strong>App Federation Metadata Url</strong> vakje, en
						plak die hieronder terug in Treams:
					</Paragraph>

					<Flex alignItems="center" mt={2}>
						<Box flexGrow={1} flexShrink={1} mr={2}>
							<Input
								placeholder="https://login.microsoftonline.com/"
								value={url}
								onChange={setURL}
							/>
						</Box>
						<Button
							title="Opslaan"
							type={step === 2 ? 'primary' : 'secondary'}
							disableSuccess={!!error}
							loading={loading || query.loading}
							onClick={onMetadataURL}
						/>
					</Flex>
					{error && <GraphQLErrorMessage error={error.graphQLErrors[0]} />}
				</Box>
			)}
			{step >= 3 && (
				<Box mb={3}>
					<Heading3>
						5. Wijs in Azure een gebruikersgroep toe om toegang te geven aan Treams.
					</Heading3>
					<Paragraph>
						Belangrijk om te vermelden: gebruikers zonder Treams-licentie worden niet gefactureerd.
						Het is dus vaak het makkelijkst om iedereen in deze Azure groep te zetten: wij checken
						altijd zelf nog of de gebruiker daadwerkelijk een Treams account heeft.
					</Paragraph>
					{step === 3 && (
						<Flex mt={2}>
							<Button title="Volgende stap" onClick={() => setStep(4)} />
						</Flex>
					)}
					{step === 4 && (
						<Box mt={2}>
							<Callout
								color="blue"
								title="Gelukt!"
								description="Het werk zit er al weer op! Nu kan iedereen gebruik maken van automatisch inloggen met Treams."
							/>
							<Flex mt={2}>
								<Button
									title="Afronden"
									disabled={!createdProviderId}
									onClick={() => history.push(`/settings/integrations/sso/${createdProviderId}`)}
								/>
							</Flex>
						</Box>
					)}
				</Box>
			)}
		</>
	);
}
